import {
  AskStrikeUpButton,
  Box,
  Button,
  Card,
  CollapsibleSection,
  Flex,
  Grid,
  GridItem,
  Heading,
  HStack,
  Input,
  Stack,
  Text,
  VStack,
} from "components/ui";
import { toaster } from "components/ui/toaster";
import { useTranslation } from "hooks/useTranslation";
import { useEffect, useRef, useState } from "react";
import {
  MdAutorenew,
  MdCheckCircle,
  MdClose,
  MdEdit,
  MdRefresh,
} from "react-icons/md";
import { useNavigate } from "react-router-dom";
import {
  ApiDocType,
  CompareDocumentationResponse,
  ServerConfig,
  serverService,
} from "services/serverService";
import { AuthorizationConfigComponent } from "./components/AuthorizationConfig";
import ServerComparisonModal from "./components/ServerComparisonModal";
import ServerSelector from "./components/ServerSelector";
import SpecUrlField, {
  SpecUrlValidationState,
} from "./components/SpecUrlField";
import ToolsList from "./components/ToolsList";

const Toolkits = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [selectedServerId, setSelectedServerId] = useState<string | undefined>(
    () => {
      const params = new URLSearchParams(window.location.search);
      return params.get("serverId") ?? undefined;
    },
  );
  const [toolsRefreshKey, setToolsRefreshKey] = useState(0);

  // Server detail state
  const [server, setServer] = useState<ServerConfig | null>(null);
  const [loadingServer, setLoadingServer] = useState(false);
  const [serverError, setServerError] = useState("");

  // Collapsible sections
  const [authOpen, setAuthOpen] = useState(false);

  // Inline editing of server details
  const [isEditing, setIsEditing] = useState(false);
  const [editedName, setEditedName] = useState("");
  const [editedDescription, setEditedDescription] = useState("");
  const [editedBaseUrl, setEditedBaseUrl] = useState("");
  const [editedSpecUrl, setEditedSpecUrl] = useState("");
  const [specUrlStatus, setSpecUrlStatus] =
    useState<SpecUrlValidationState>("idle");
  const [savingDetails, setSavingDetails] = useState(false);

  // Documentation comparison
  const [checkingUpdates, setCheckingUpdates] = useState(false);
  const [comparisonResult, setComparisonResult] =
    useState<CompareDocumentationResponse | null>(null);
  const [showComparisonModal, setShowComparisonModal] = useState(false);

  // Manual capabilities rebuild — a fallback only. Every toolkit change already rebuilds the
  // capabilities automatically on the backend; this button is for when that failed.
  const [regeneratingCapabilities, setRegeneratingCapabilities] =
    useState(false);

  // Sticky banner refs
  const [selectorInView, setSelectorInView] = useState(true);
  const [bannerLeft, setBannerLeft] = useState(0);
  const selectorRef = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  // Track selector visibility for sticky banner
  useEffect(() => {
    const check = () => {
      const el = selectorRef.current;
      if (!el) return;
      setSelectorInView(el.getBoundingClientRect().bottom > 0);
    };
    check();
    window.addEventListener("scroll", check, { passive: true });
    return () => window.removeEventListener("scroll", check);
  }, []);

  // Track content area left offset for the sticky banner
  useEffect(() => {
    const update = () => {
      if (contentRef.current) {
        setBannerLeft(contentRef.current.getBoundingClientRect().left);
      }
    };
    update();
    window.addEventListener("resize", update);
    return () => window.removeEventListener("resize", update);
  }, []);

  // Load server details when selection changes
  useEffect(() => {
    if (!selectedServerId) {
      setServer(null);
      return;
    }
    const load = async () => {
      try {
        setLoadingServer(true);
        setServerError("");
        const data = await serverService.getServerById(selectedServerId);
        setServer(data);
        setEditedName(data.name);
        setEditedDescription(data.description ?? "");
        setEditedBaseUrl(data.baseUrl ?? "");
        setEditedSpecUrl(data.specUrl ?? "");
        setSpecUrlStatus("idle");
      } catch (err: any) {
        setServerError(
          err.response?.data?.message ||
            t("app.servers.errors.failedToLoadDetails"),
        );
      } finally {
        setLoadingServer(false);
      }
    };
    load();
    // Reset UI state on selection change
    setIsEditing(false);
    setAuthOpen(false);
  }, [selectedServerId, t]);

  const handleSelect = (id: string) => {
    setSelectedServerId(id || undefined);
    const params = new URLSearchParams(window.location.search);
    if (id) {
      params.set("serverId", id);
    } else {
      params.delete("serverId");
    }
    navigate({ search: params.toString() }, { replace: true });
  };

  // The spec URL is saved through its own endpoint, and only when the admin actually moved it.
  const specUrlChanged =
    editedSpecUrl.trim() !== (server?.specUrl ?? "").trim();

  const handleSaveDetails = async () => {
    if (!editedName.trim()) {
      toaster.create({
        title: t("app.servers.errors.nameRequired"),
        type: "error",
      });
      return;
    }
    if (specUrlChanged && specUrlStatus !== "valid") {
      toaster.create({
        title: t("app.servers.specUrlNotValidated"),
        type: "error",
      });
      return;
    }
    try {
      setSavingDetails(true);
      // Repoint the documentation first — a rejected URL must not leave the name and description
      // saved against a document the toolkit can no longer read.
      if (specUrlChanged) {
        await serverService.updateSpecUrl(selectedServerId!, {
          specUrl: editedSpecUrl.trim(),
        });
      }
      const updated = await serverService.updateServer(selectedServerId!, {
        name: editedName,
        description: editedDescription,
        baseUrl: editedBaseUrl,
      });
      setServer(updated);
      setEditedSpecUrl(updated.specUrl ?? "");
      setSpecUrlStatus("idle");
      setIsEditing(false);
      toaster.create({
        title: specUrlChanged
          ? t("app.servers.specUrlUpdated")
          : t("app.servers.detailsUpdated"),
        description: specUrlChanged
          ? t("app.servers.specUrlUpdatedDesc")
          : undefined,
        type: "success",
      });
    } catch (err: any) {
      toaster.create({
        title: specUrlChanged
          ? t("app.servers.errors.specUrlUpdateFailed")
          : t("app.servers.errors.failedToUpdate"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    } finally {
      setSavingDetails(false);
    }
  };

  const handleCancelEdit = () => {
    setEditedName(server?.name ?? "");
    setEditedDescription(server?.description ?? "");
    setEditedBaseUrl(server?.baseUrl ?? "");
    setEditedSpecUrl(server?.specUrl ?? "");
    setSpecUrlStatus("idle");
    setIsEditing(false);
  };

  const handleCheckForUpdates = async () => {
    try {
      setCheckingUpdates(true);
      const result = await serverService.compareDocumentation(
        selectedServerId!,
      );
      setComparisonResult(result);
      if (result.hasChanges) {
        setShowComparisonModal(true);
      } else {
        toaster.create({
          title: t("app.servers.documentationComparison.noChanges"),
          type: "success",
        });
      }
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.documentationComparison.errors.checkFailed"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    } finally {
      setCheckingUpdates(false);
    }
  };

  const handleRegenerateCapabilities = async () => {
    try {
      setRegeneratingCapabilities(true);
      const refreshed = await serverService.regenerateCapabilities(
        selectedServerId!,
      );
      setServer(refreshed);
      toaster.create({
        title: t("app.servers.capabilities.regenerateSuccess"),
        type: "success",
      });
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.capabilities.regenerateError"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    } finally {
      setRegeneratingCapabilities(false);
    }
  };

  const handleUpdatesApplied = async () => {
    try {
      const updated = await serverService.getServerById(selectedServerId!);
      setServer(updated);
      setShowComparisonModal(false);
      setComparisonResult(null);
    } catch (err: any) {
      toaster.create({
        title: t("app.servers.errors.reloadFailed"),
        description: err.response?.data?.message || err.message,
        type: "error",
      });
    }
  };

  return (
    <Stack gap={5} w="full" ref={contentRef}>
      {/* Page header */}
      <Flex justify="space-between" align="center" wrap="wrap" gap={4} mb={2}>
        <VStack align="start" gap={1}>
          <Heading size="lg" color="var(--text-primary)">
            Toolkits
          </Heading>
          <Text fontSize="sm" color="var(--text-secondary-color)" m={0}>
            Connect and configure your API toolkits.
          </Text>
        </VStack>
        <AskStrikeUpButton context={t("app.askStrikeUp.context.servers")} />
      </Flex>

      {/* Toolkit selector */}
      <Box ref={selectorRef}>
        <ServerSelector
          selectedServerId={selectedServerId}
          onSelect={handleSelect}
          onGenerationComplete={(serverId) => {
            if (serverId === selectedServerId) {
              setToolsRefreshKey((k) => k + 1);
            }
          }}
        />
      </Box>

      {/* Sticky context banner */}
      {selectedServerId && !loadingServer && server && !selectorInView && (
        <HStack
          position="fixed"
          top={0}
          left={`${bannerLeft}px`}
          right={0}
          zIndex={49}
          px={4}
          py={2}
          bg={{ base: "blue.50", _dark: "blue.950" }}
          borderBottomWidth="1px"
          borderColor={{ base: "blue.200", _dark: "blue.700" }}
          gap={2}
          boxShadow="sm"
        >
          <Text fontSize="xs" color={{ base: "blue.600", _dark: "blue.300" }}>
            Editing toolkit
          </Text>
          <Text
            fontSize="xs"
            fontWeight="semibold"
            color={{ base: "blue.700", _dark: "blue.200" }}
          >
            {server.name}
          </Text>
        </HStack>
      )}

      {/* Empty state */}
      {!selectedServerId ? (
        <Card.Root>
          <Card.Body>
            <Text
              textAlign="center"
              color="var(--text-secondary-color)"
              py={6}
              m={0}
            >
              Select a toolkit above to view and configure its settings.
            </Text>
          </Card.Body>
        </Card.Root>
      ) : loadingServer ? (
        <Card.Root>
          <Card.Body>
            <Text textAlign="center" color="var(--text-primary)" m={0}>
              Loading…
            </Text>
          </Card.Body>
        </Card.Root>
      ) : serverError ? (
        <Box
          p={3}
          borderRadius="10px"
          bg="rgba(239, 68, 68, 0.08)"
          borderWidth="1px"
          borderColor="var(--accent-danger, #EF4444)"
        >
          <Text color="var(--text-primary)" fontSize="sm" m={0}>
            {serverError}
          </Text>
        </Box>
      ) : server ? (
        <>
          {/* Server Details */}
          <Card.Root overflow="hidden" w="full">
            <Card.Body p={5}>
              <HStack justify="space-between" mb={3} align="center">
                <VStack align="start" gap={0.5}>
                  <Text
                    fontFamily="var(--font-mono)"
                    fontSize="10px"
                    letterSpacing="0.10em"
                    textTransform="uppercase"
                    color="var(--text-secondary-color)"
                    m={0}
                  >
                    Details
                  </Text>
                  <Heading size="md" color="var(--text-primary)" m={0}>
                    {t("app.servers.serverDetails")}
                  </Heading>
                </VStack>
                <HStack gap={2}>
                  {server.authorizationType === "CustomAuthEndpoint" &&
                    !isEditing && (
                      <Button
                        onClick={() =>
                          navigate(
                            `/admin/servers/authorization-logs/${selectedServerId}`,
                          )
                        }
                        intent="secondary"
                        size="sm"
                      >
                        <MdCheckCircle />
                        {t("app.servers.authorization.logs.viewLogs")}
                      </Button>
                    )}
                  {!isEditing && (
                    <Button
                      onClick={handleRegenerateCapabilities}
                      intent="secondary"
                      size="sm"
                      loading={regeneratingCapabilities}
                      disabled={regeneratingCapabilities}
                    >
                      <MdAutorenew />
                      {t("app.servers.capabilities.regenerateButton")}
                    </Button>
                  )}
                  {!isEditing && (
                    <Button
                      onClick={handleCheckForUpdates}
                      intent="secondary"
                      size="sm"
                      loading={checkingUpdates}
                      disabled={checkingUpdates}
                    >
                      <MdRefresh />
                      {t("app.servers.documentationComparison.checkForUpdates")}
                    </Button>
                  )}
                  {!isEditing && (
                    <Button
                      onClick={() => setIsEditing(true)}
                      intent="secondary"
                      size="sm"
                    >
                      <MdEdit />
                      {t("app.actions.edit")}
                    </Button>
                  )}
                </HStack>
              </HStack>

              {isEditing ? (
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={3}
                  w="full"
                >
                  <GridItem>
                    <Text
                      fontSize="sm"
                      fontWeight={500}
                      mb={1}
                      color="var(--text-primary)"
                    >
                      {t("app.servers.name")}
                    </Text>
                    <Input
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      placeholder={t("app.servers.namePlaceholder")}
                      h="40px"
                    />
                  </GridItem>
                  <GridItem>
                    <Text
                      fontSize="sm"
                      fontWeight={500}
                      mb={1}
                      color="var(--text-primary)"
                    >
                      {t("app.servers.baseUrl")}
                    </Text>
                    <Input
                      value={editedBaseUrl}
                      onChange={(e) => setEditedBaseUrl(e.target.value)}
                      placeholder={t("app.servers.baseUrlPlaceholder")}
                      h="40px"
                    />
                  </GridItem>
                  <GridItem colSpan={{ base: 1, md: 2 }}>
                    <SpecUrlField
                      value={editedSpecUrl}
                      storedValue={server.specUrl ?? ""}
                      apiDocType={server.apiDocType ?? ApiDocType.Swagger}
                      onChange={setEditedSpecUrl}
                      onValidationStateChange={setSpecUrlStatus}
                    />
                  </GridItem>
                  <GridItem colSpan={{ base: 1, md: 2 }}>
                    <Text
                      fontSize="sm"
                      fontWeight={500}
                      mb={1}
                      color="var(--text-primary)"
                    >
                      {t("app.servers.description")}
                    </Text>
                    <Input
                      value={editedDescription}
                      onChange={(e) => setEditedDescription(e.target.value)}
                      placeholder={t("app.servers.descriptionPlaceholder")}
                      h="40px"
                    />
                  </GridItem>
                </Grid>
              ) : (
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={3}
                  w="full"
                >
                  <GridItem>
                    <Text
                      fontFamily="var(--font-mono)"
                      fontSize="10px"
                      letterSpacing="0.10em"
                      textTransform="uppercase"
                      color="var(--text-secondary-color)"
                      mb={1}
                    >
                      {t("app.servers.name")}
                    </Text>
                    <Text
                      color="var(--text-primary)"
                      wordBreak="break-word"
                      m={0}
                    >
                      {server.name}
                    </Text>
                  </GridItem>
                  <GridItem>
                    <Text
                      fontFamily="var(--font-mono)"
                      fontSize="10px"
                      letterSpacing="0.10em"
                      textTransform="uppercase"
                      color="var(--text-secondary-color)"
                      mb={1}
                    >
                      {t("app.servers.baseUrl")}
                    </Text>
                    <Text
                      color="var(--text-primary)"
                      wordBreak="break-word"
                      m={0}
                    >
                      {server.baseUrl}
                    </Text>
                  </GridItem>
                  {server.specUrl && (
                    <GridItem colSpan={{ base: 1, md: 2 }}>
                      <Text
                        fontFamily="var(--font-mono)"
                        fontSize="10px"
                        letterSpacing="0.10em"
                        textTransform="uppercase"
                        color="var(--text-secondary-color)"
                        mb={1}
                      >
                        {t("app.servers.specUrl")}
                      </Text>
                      <Text
                        color="var(--text-primary)"
                        wordBreak="break-all"
                        m={0}
                      >
                        {server.specUrl}
                      </Text>
                    </GridItem>
                  )}
                  <GridItem colSpan={{ base: 1, md: 2 }}>
                    <Text
                      fontFamily="var(--font-mono)"
                      fontSize="10px"
                      letterSpacing="0.10em"
                      textTransform="uppercase"
                      color="var(--text-secondary-color)"
                      mb={1}
                    >
                      {t("app.servers.description")}
                    </Text>
                    <Text
                      color="var(--text-primary)"
                      wordBreak="break-word"
                      m={0}
                    >
                      {server.description}
                    </Text>
                  </GridItem>
                </Grid>
              )}

              {isEditing && (
                <HStack justify="flex-end" mt={4} gap={2}>
                  <Button
                    onClick={handleCancelEdit}
                    intent="secondary"
                    size="sm"
                  >
                    <MdClose />
                    {t("app.actions.cancel")}
                  </Button>
                  <Button
                    onClick={handleSaveDetails}
                    intent="primary"
                    size="sm"
                    loading={savingDetails}
                    disabled={
                      savingDetails ||
                      (specUrlChanged && specUrlStatus !== "valid")
                    }
                  >
                    <MdCheckCircle />
                    {t("app.actions.save")}
                  </Button>
                </HStack>
              )}
            </Card.Body>
          </Card.Root>

          {/* Authorization */}
          <CollapsibleSection
            pre="Auth"
            title={t("app.servers.authorization.title")}
            isOpen={authOpen}
            onOpenChange={setAuthOpen}
          >
            <AuthorizationConfigComponent
              serverId={selectedServerId}
              onServerUpdated={(updated) => setServer(updated)}
            />
          </CollapsibleSection>

          {/* Tools */}
          <ToolsList serverId={selectedServerId} refreshKey={toolsRefreshKey} />

          {/* Prompts — hidden for now, kept for future use */}
          {/* <PromptsList serverId={selectedServerId} /> */}

          {/* Comparison modal */}
          {comparisonResult && (
            <ServerComparisonModal
              isOpen={showComparisonModal}
              onClose={() => setShowComparisonModal(false)}
              serverId={selectedServerId}
              comparisonResult={comparisonResult}
              onUpdatesApplied={handleUpdatesApplied}
            />
          )}
        </>
      ) : null}
    </Stack>
  );
};

export default Toolkits;
